import { useMemo, useEffect, useState } from 'react'
import { Box, Drawer, Typography } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectBeacon4VisibleSeriesLeft,
  selectBeacon4VisibleSeriesRight,
  toggleBeacon4PanelSeries,
  resetBeacon4HiddenSeries,
  selectBeacon4PerfTech,
  selectBeacon4PerfMainKpi,
} from '../../reducers/Beacon4FiltersReducer'
import { selectBeacon4PerformanceDashboardByTech } from '../../reducers/Beacon4PerformanceKpiDataReducer'

// Distinct hues for Performance KPI series (chosen for clear differentiation)
const PALETTE = [
  '#1565c0', '#2e7d32', '#f57c00', '#6a1a9a', '#c62828',
  '#00897b', '#5d4037', '#ad1457', '#0277bd', '#7b1fa2',
]

function getColorForKey(key, idx) {
  return PALETTE[idx % PALETTE.length]
}

function LegendItem({ d, isHidden, onClick, shimmer }) {
  return (
    <Box onClick={() => onClick(d.key)} sx={{
      position: 'relative', overflow: 'hidden',
      display: 'flex', alignItems: 'center', gap: 1.25,
      px: 1, py: 0.75, borderRadius: 1, cursor: 'pointer',
      opacity: isHidden ? 0.3 : 1,
      transition: 'opacity 0.2s, background 0.15s',
      '&:hover': { bgcolor: 'action.hover' },
      '&::after': shimmer ? {
        content: '""',
        position: 'absolute',
        top: 0, left: '-100%',
        width: '60%', height: '100%',
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)',
        transform: 'skewX(-20deg)',
        animation: `b4p-shimmer 2.2s linear infinite`,
        animationDelay: `${d._delay ?? 0}ms`,
      } : {},
      '@keyframes b4p-shimmer': {
        '0%':   { left: '-100%' },
        '100%': { left: '150%'  },
      },
    }}>
      <Box sx={{
        width: 24, flexShrink: 0,
        height: 3, borderRadius: 1, bgcolor: d.color,
      }} />
      <Typography sx={{
        fontSize: 12, fontWeight: isHidden ? 400 : 600,
        color: isHidden ? 'text.disabled' : d.color,
        lineHeight: 1.2,
      }}>
        {d.label}
      </Typography>
    </Box>
  )
}

export default function Beacon4PerformanceLegendPanel({ width = 220 }) {
  const dispatch        = useDispatch()
  const visibleLeft     = useSelector(selectBeacon4VisibleSeriesLeft)
  const visibleRight    = useSelector(selectBeacon4VisibleSeriesRight)
  const dashboardByTech = useSelector(selectBeacon4PerformanceDashboardByTech)
  const tech            = useSelector(selectBeacon4PerfTech)
  const mainKpiKey      = useSelector(selectBeacon4PerfMainKpi)
  const handleClick     = (panel, key) => dispatch(toggleBeacon4PanelSeries({ panel, key }))

  const [hasData, setHasData] = useState(false)

  // Extract current dashboard data
  const data       = dashboardByTech?.[tech]?.[mainKpiKey]
  const leftPanel  = data?.leftPanel  ?? []
  const rightPanel = data?.rightPanel ?? []

  // Build legend items keyed by KPI name
  const leftItems = useMemo(() => (
    leftPanel.map((s, i) => ({
      key: s.kpiName, label: s.kpiName,
      color: getColorForKey(s.kpiName, i),
      _delay: i * 200,
    }))
  ), [leftPanel])

  const rightItems = useMemo(() => (
    rightPanel.map((s, i) => ({
      key: s.kpiName, label: s.kpiName,
      color: getColorForKey(s.kpiName, leftItems.length + i),
      _delay: (leftItems.length + i) * 200,
    }))
  ), [rightPanel, leftItems])

  useEffect(() => {
    if (leftPanel.length > 0 || rightPanel.length > 0) setHasData(true)
  }, [leftPanel, rightPanel])

  const showShimmer = hasData && visibleLeft === null && visibleRight === null
  const showHint    = hasData && visibleLeft === null && visibleRight === null

  if (!hasData) return null

  return (
    <Drawer anchor="right" open variant="persistent" sx={{
      flexShrink: 0,
      '& .MuiDrawer-paper': {
        width, position: 'relative', height: '100%',
        border: 'none', borderLeft: '1px solid', borderColor: 'divider',
        bgcolor: '#fff', overflow: 'auto',
      },
    }}>
      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.25, flex: 1 }}>

        {/* Hint — top, prominent */}
        {showHint && (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            px: 1.25, py: 1, mb: 1, borderRadius: 1.5,
            bgcolor: 'primary.main',
            animation: 'b4p-hint 1.6s ease-in-out infinite',
            '@keyframes b4p-hint': {
              '0%':   { opacity: 0.8, transform: 'translateX(0px)'  },
              '50%':  { opacity: 1,   transform: 'translateX(4px)'  },
              '100%': { opacity: 0.8, transform: 'translateX(0px)'  },
            },
          }}>
            <Box component="svg" viewBox="0 0 24 24" fill="none"
              stroke="white" strokeWidth={2.5}
              sx={{ width: 16, height: 16, flexShrink: 0 }}>
              <path d="M5 12h14" />
              <path d="m13 5 7 7-7 7" />
            </Box>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#fff', lineHeight: 1.3 }}>
              Click a label to filter
            </Typography>
          </Box>
        )}

        {/* LEFT panel section */}
        {leftItems.length > 0 && (
          <>
            <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em', px: 1, mb: 0.5, mt: 0.5 }}>
              KPI Performance
            </Typography>
            {leftItems.map(d => (
              <LegendItem
                key={d.key} d={d}
                isHidden={visibleLeft !== null && !visibleLeft.includes(d.key)}
                onClick={(k) => handleClick('left', k)}
                shimmer={showShimmer}
              />
            ))}
          </>
        )}

        {/* RIGHT panel section */}
        {rightItems.length > 0 && (
          <>
            <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em', px: 1, mt: 1.5, mb: 0.5 }}>
              KPI Failures
            </Typography>
            {rightItems.map(d => (
              <LegendItem
                key={d.key} d={d}
                isHidden={visibleRight !== null && !visibleRight.includes(d.key)}
                onClick={(k) => handleClick('right', k)}
                shimmer={showShimmer}
              />
            ))}
          </>
        )}
      </Box>

      {/* Show all */}
      <Box sx={{ px: 1.5, pb: 1.5 }}>
        {(visibleLeft !== null || visibleRight !== null) && (
          <Box onClick={() => dispatch(resetBeacon4HiddenSeries())} sx={{
            fontSize: 11, fontWeight: 600, color: 'primary.main', cursor: 'pointer',
            textAlign: 'center', py: 0.75, borderRadius: 1,
            border: '1px solid', borderColor: 'primary.light',
            '&:hover': { bgcolor: 'primary.light' }, transition: 'all .15s',
          }}>
            ✕ Show all
          </Box>
        )}
      </Box>
    </Drawer>
  )
}

// Export the palette helper so the view can match the legend colours
export { getColorForKey, PALETTE }